import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { message, notification } from 'antd';
import { API_CONFIG } from '@/config/apiConfig';
import { AuthService } from '@/services/auth';
import { getCookie } from '@/utils/cookie';

/**
 * 错误显示类型枚举
 * 定义了不同类型错误的展示方式
 */
enum ErrorShowType {
  SILENT = 0, // 静默处理，不显示错误信息
  WARN_MESSAGE = 1, // 显示警告消息
  ERROR_MESSAGE = 2, // 显示错误消息
  NOTIFICATION = 3, // 显示通知
  REDIRECT = 9, // 重定向处理
}

/**
 * 标准HTTP状态码映射
 * 根据RFC 7231等标准定义
 */
const HTTP_STATUS = {
  BAD_REQUEST: 400, // 请求语法错误
  UNAUTHORIZED: 401, // 未授权
  FORBIDDEN: 403, // 禁止访问
  NOT_FOUND: 404, // 资源未找到
  METHOD_NOT_ALLOWED: 405, // 方法不允许
  REQUEST_TIMEOUT: 408, // 请求超时
  CONFLICT: 409, // 冲突
  INTERNAL_SERVER_ERROR: 500, // 服务器内部错误
  BAD_GATEWAY: 502, // 网关错误
  SERVICE_UNAVAILABLE: 503, // 服务不可用
  GATEWAY_TIMEOUT: 504, // 网关超时
} as const;

/**
 * 与后端约定的响应数据格式
 * 遵循RESTful API设计规范
 */
interface ResponseStructure {
  success: boolean; // 请求是否成功
  data: any; // 响应数据
  errorCode?: number; // 业务错误码
  errorMessage?: string; // 错误信息
  showType?: ErrorShowType; // 错误显示类型
  timestamp?: number; // 时间戳
  path?: string; // 请求路径
}

/**
 * 标准错误消息映射
 * 提供用户友好的错误提示
 */
const ERROR_MESSAGES = {
  [HTTP_STATUS.BAD_REQUEST]: '请求参数错误，请检查输入信息',
  [HTTP_STATUS.UNAUTHORIZED]: '登录已过期，请重新登录',
  [HTTP_STATUS.FORBIDDEN]: '没有权限访问该资源',
  [HTTP_STATUS.NOT_FOUND]: '请求的资源不存在',
  [HTTP_STATUS.METHOD_NOT_ALLOWED]: '请求方法不被允许',
  [HTTP_STATUS.REQUEST_TIMEOUT]: '请求超时，请稍后重试',
  [HTTP_STATUS.CONFLICT]: '数据冲突，请刷新后重试',
  [HTTP_STATUS.INTERNAL_SERVER_ERROR]: '服务器内部错误，请联系管理员',
  [HTTP_STATUS.BAD_GATEWAY]: '网关错误，请稍后重试',
  [HTTP_STATUS.SERVICE_UNAVAILABLE]: '服务暂时不可用，请稍后重试',
  [HTTP_STATUS.GATEWAY_TIMEOUT]: '网关超时，请稍后重试',
} as const;

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const { success, data, errorCode, errorMessage, showType } =
        res as unknown as ResponseStructure;
      if (success === false) {
        const error: any = new Error(errorMessage);
        error.name = 'BizError';
        error.info = { errorCode, errorMessage, showType, data };
        throw error; // 抛出自制的错误
      }
    },
    /**
     * 错误处理器
     * 统一处理各种类型的错误，提供标准化的用户反馈
     *
     * 错误处理规则：
     * 1. 业务错误（BizError）：根据showType显示不同类型的提示
     * 2. HTTP错误：根据状态码显示标准化错误信息
     * 3. 网络错误：显示网络相关错误提示
     * 4. 未知错误：显示通用错误提示
     */
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;

      // 处理业务逻辑错误
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode, showType } = errorInfo;

          switch (showType) {
            case ErrorShowType.SILENT:
              // 静默处理，不显示任何提示
              console.warn('Silent error:', errorMessage);
              break;

            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMessage || '操作警告');
              break;

            case ErrorShowType.ERROR_MESSAGE:
              message.error(errorMessage || '操作失败');
              break;

            case ErrorShowType.NOTIFICATION:
              notification.error({
                message: `错误 ${errorCode || ''}`,
                description: errorMessage || '系统发生错误',
                duration: 4.5,
              });
              break;

            case ErrorShowType.REDIRECT:
              // 处理需要重定向的错误（如登录过期）
              message.error(errorMessage || '登录已过期，请重新登录');
              AuthService.redirectToLogin({ delayMs: 1000 });
              break;

            default:
              message.error(errorMessage || '未知错误');
          }
        }
      }
      // 处理HTTP响应错误
      else if (error.response) {
        const status = error.response.status;
        const errorMessage =
          ERROR_MESSAGES[status as keyof typeof ERROR_MESSAGES] ||
          `服务器错误 (${status})`;

        // 特殊处理401未授权错误
        if (status === HTTP_STATUS.UNAUTHORIZED) {
          message.error(errorMessage);
          // 先清除cookie，避免来回跳转
          AuthService.redirectToLogin({ delayMs: 1000 });
        } else {
          message.error(errorMessage);
        }

        // 开发环境下输出详细错误信息
        if (process.env.NODE_ENV === 'development') {
          console.error('HTTP Error:', {
            status,
            statusText: error.response.statusText,
            data: error.response.data,
            config: error.config,
          });
        }
      }
      // 处理网络请求错误（无响应）
      else if (error.request) {
        message.error('网络连接失败，请检查网络设置后重试');
        console.error('Network Error:', error.request);
      }
      // 处理其他未知错误
      else {
        message.error('请求发送失败，请稍后重试');
        console.error('Unknown Error:', error.message);
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      // 拦截请求配置，进行个性化处理。
      const token = getCookie('admin_access_token');
      const url = config.url || '';

      // 判断是否是需要鉴权的请求
      const isApiRequest = url.startsWith('/api');
      const isManageRequest = url.startsWith('/manage/');
      const isWebhookRequest = url.startsWith('/webhook/');
      const isProductService =
        API_CONFIG.PRODUCT_API_BASE &&
        url.startsWith(API_CONFIG.PRODUCT_API_BASE);
      const isEcsService =
        API_CONFIG.ECS_API_BASE && url.startsWith(API_CONFIG.ECS_API_BASE);
      const isN8nService =
        API_CONFIG.N8N_API_BASE && url.startsWith(API_CONFIG.N8N_API_BASE);
      const isManageService =
        API_CONFIG.MODEL_MANAGE_API_BASE &&
        url.startsWith(API_CONFIG.MODEL_MANAGE_API_BASE);
      const isContestService =
        API_CONFIG.CONTEST_API_BASE &&
        url.startsWith(API_CONFIG.CONTEST_API_BASE);
      const isContestPreviewService =
        API_CONFIG.MODEL_MANAGE_API_BASE_PREVIEW &&
        url.startsWith(API_CONFIG.MODEL_MANAGE_API_BASE_PREVIEW);

      const needsAuth =
        isApiRequest ||
        isManageRequest ||
        isWebhookRequest ||
        isProductService ||
        isEcsService ||
        isN8nService ||
        isManageService ||
        isContestService ||
        isContestPreviewService;

      if (token && needsAuth) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        };

        const MODEL_SERVICE_PATTERNS = ['/api/model', '/api/files/'];
        const isModelService = MODEL_SERVICE_PATTERNS.some((p) =>
          url.startsWith(p),
        );

        if (
          isModelService ||
          isManageService ||
          isContestService ||
          isContestPreviewService
        ) {
          if (isModelService && !isManageService) {
            const isDev =
              window.location.hostname === 'localhost' ||
              window.location.hostname === '127.0.0.1';
            if (!isDev && API_CONFIG.MODEL_API_BASE) {
              config.url = `${API_CONFIG.MODEL_API_BASE}${url}`;
            }
          }
          try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const userId = payload.sub || payload.user_id || payload.id || '1';
            config.headers = {
              ...config.headers,
              'x-user-id': String(userId),
            };
          } catch {
            config.headers = { ...config.headers, 'x-user-id': '1' };
          }
        }
      }
      return config;
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      // 拦截响应数据，进行个性化处理
      const { data } = response as unknown as ResponseStructure;

      if (data?.success === false) {
        message.error('请求失败！');
      }
      return response;
    },
  ],
};
